import type { QueueResponse } from './ApiClient';
import { FRONTEND_ERROR_API_NAME, FRONTEND_ERROR_CODE, TIMEOUT_ERROR_NAME } from './errors';
import type { ResponseError, ResponseSuccess } from './HttpClient';
import { JOB_COMPLETED, JOB_FAILED, JOB_FAILED_CODE, JOB_FAILED_NAME } from './job';

type JobResponseError = ResponseError & {
  jobId?: string;
};

type JobResult<T extends QueueResponse> = JobResponseError | ResponseSuccess<T>;

export type JobRequestHandlerProps<T extends QueueResponse> = {
  initFn: () => Promise<JobResult<T>>;
  statusFn: (jobId: string) => Promise<JobResult<T>>;
  setResult: (r: JobResult<T> | null) => void;
  pollingDelayMs?: number;
  signal?: AbortSignal;
};

// {  "name": "TimeoutError",  "message": "Timeout waiting for job completion",  "jobId": "754eef79-ec82-4af7-9114-91cdc06591f4"}

export class JobRequestHandler<T extends QueueResponse> {
  private initFn: () => Promise<JobResult<T>>;
  private statusFn: (jobId: string) => Promise<JobResult<T>>;
  private setResult: (r: JobResult<T> | null) => void;
  private pollingDelay: number;

  /**
   * AbortController used to signal cancellation.
   * Callers whose initFn/statusFn accept an AbortSignal should read
   * `handler.signal` and pass it to their fetch calls so in-flight HTTP
   * requests are also aborted.
   */
  public readonly abortController: AbortController = new AbortController();
  private cancelled = false;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;

  /** Expose the AbortSignal so callers can wire it into their fetch calls. */
  get signal(): AbortSignal {
    return this.abortController.signal;
  }

  constructor({
    initFn,
    statusFn,
    setResult,
    pollingDelayMs = 100,
    signal,
  }: JobRequestHandlerProps<T>) {
    this.initFn = initFn;
    this.statusFn = statusFn;
    this.setResult = setResult;
    this.pollingDelay = pollingDelayMs;
    if (signal) {
      AbortSignal.any([this.abortController.signal, signal]).addEventListener('abort', () =>
        this.abortController.abort()
      );
    }
  }

  public async start() {
    if (this.cancelled || this.signal.aborted) return;
    const result = await this.initFn();

    if (result.ok) {
      this.setResult({
        ok: true,
        data: result!.data,
      });
    } else {
      if (result.name === TIMEOUT_ERROR_NAME && result.jobId) {
        this.poll(result.jobId);
      } else {
        this.setResult({
          ok: false,
          code: result.code,
          timestamp: result.timestamp,
          name: result.name,
          ...(result.message !== undefined && { message: result.message }),
        });
      }
    }
  }

  private async poll(jobId: string) {
    if (this.cancelled || this.signal.aborted) {
      return;
    }

    try {
      const result = await this.statusFn(jobId);

      // Discard result if cancelled while the request was in-flight
      if (this.cancelled || this.signal.aborted) {
        return;
      }

      if (!result.ok) {
        this.setResult({
          ok: false,
          code: result.code,
          timestamp: result.timestamp,
          name: result.name,
          ...(result.message !== undefined && { message: result.message }),
        });
        return;
      }

      const jobStatus = result.data.status;

      if (jobStatus === JOB_COMPLETED) {
        this.setResult({
          ok: true,
          data: result!.data,
        });
        // Job failed -> Receive a success converted into error in the front !
      } else if (jobStatus === JOB_FAILED) {
        this.setResult({
          ok: false,
          code: JOB_FAILED_CODE,
          timestamp: result.data.timestamp,
          name: JOB_FAILED_NAME,
          message: result.data.message,
        });
        // status : pending or processing
      } else {
        this.pollTimer = setTimeout(() => this.poll(jobId), this.pollingDelay);
      }
    } catch (err) {
      if (this.cancelled || this.signal.aborted) {
        return;
      }
      this.setResult({
        ok: false,
        code: FRONTEND_ERROR_CODE,
        name: FRONTEND_ERROR_API_NAME,
        timestamp: new Date().toISOString(),
        message: (err as Error).message,
      });
    }
  }

  /**
   * Cancel any pending poll and prevent further setResult calls.
   *
   * Note: this stops the polling loop and signals the AbortController.
   * In-flight HTTP requests are only aborted if the caller wired `handler.signal`
   * into their initFn/statusFn fetch calls, because those functions are provided
   * externally and JobRequestHandler has no direct reference to the underlying
   * fetch Promise.
   */
  cancel(): void {
    this.cancelled = true;
    if (this.pollTimer !== null) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
    this.abortController.abort();
  }
}
